// YOKOSO 運営 (プラットフォーム管理者) 専用 API (#1138): budgets/alerts/退会監査/
// owner usage。店舗オーナー向けは storeOwner.ts へ。配置ルールは index.ts 参照。
import { apiFetch } from './_client'

/** #2024 返金が失敗している予約 (運営の一覧の 1 行)。 */
export interface AdminFailedRefund {
  reservationId: string
  storeId: string
  storeName: string
  date: string | null
  time: string | null
  status: string | null
  paymentMethod: 'card' | 'points'
  outstanding: number
  refundError: string | null
  autoRetryCount: number
  autoRetryAt: string | null
  cancelledAt: string | null
  needsHuman: boolean
  reason: 'needs_check' | 'retry_limit' | 'auto_retrying'
}

/** #2308 ② オンラインの返金で、お客様が「届いていない」と申し出たもの (運営が突き合わせる) */
export interface AdminOnlineRefundClaim {
  voucherId: string
  storeId: string
  storeName: string
  amount: number
  kind: 'reservation' | 'ticket' | 'sale'
  claimNote: string | null
  online: import('./reservations').RefundOnline | null
  reservation?: { id: string; shortCode?: string; date?: string; time?: string; partySize?: number; contactName?: string } | null
  ticket?: { id: string; productName?: string; slotDate?: string; slotStartTime?: string; quantity?: number } | null
  sale?: { id: string; amount?: number; refundedAt?: string | null; createdAt?: string | null } | null
}

/** #2298 返金が失敗したままの体験のチケット (運営の一覧の 1 行)。自動では出し直さない */
export interface AdminFailedTicketRefund {
  ticketId: string
  storeId: string
  storeName: string
  productName: string | null
  slotDate: string | null
  slotStartTime: string | null
  outstanding: number
  refundError: string | null
  /** カードの解約など (引換券がまだ無い。翌朝のジョブが出す) */
  cardUnreachable: boolean
  cancelledAt: string | null
}

/** #2121 代行で載せている店の 1 件 (運営の一覧)。許諾・引き渡し先・担当者メモは
 *  公開の店舗 API には出ない (別コレクション `store_agency_listings`)。 */
export interface AdminAgencyListing {
  storeId: string
  storeName: string
  storeMissing: boolean
  listingManagedBy: 'store' | 'agency'
  discoverability: string
  orderingMode: string
  consentAt: string
  consentFrom: string
  consentChannel: string
  consentNote: string
  handoverEmail: string
  staffNote: string
  checkedAt: string
  handedOverAt: string
  updatedAt: string
  updatedBy: string
}

export interface AdminAgencyListingInput {
  consentAt: string
  consentFrom: string
  consentChannel: string
  consentNote: string
  handoverEmail: string
  staffNote: string
  checkedAt: string
}

export const adminApi = {
  // #52 admin-only: platform AI budget read / set
  adminWhoami: async (idToken: string): Promise<{ uid: string; email: string | null; isAdmin: boolean }> => {
    const res = await apiFetch('/admin/whoami', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  adminGetAiBudget: async (idToken: string) => {
    const res = await apiFetch('/admin/ai-budget', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  adminGetShares: async (idToken: string) => {
    const res = await apiFetch('/admin/shares', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  // 1 オーナーあたりの月次 AI 上限。総予算をオーナー数で割る方式はやめたので、
  // 名前も総額と取り違えないものにしてある。
  adminSetAiBudget: async (perOwnerMonthlyCapJpy: number, idToken: string) => {
    const res = await apiFetch('/admin/ai-budget', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ perOwnerMonthlyCapJpy }),
    })
    return res.json()
  },
  adminRegenerateSnapshot: async (idToken: string) => {
    const res = await apiFetch('/admin/ai-budget/regenerate-snapshot', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #296 admin: Firestore budget dashboard + mutations
  // #2338 EC の決済上限。**出費の予算ではなく不正利用への防御**なので、
  // 変更は誰がいつやったかがサーバ側に残る
  adminGetEcPaymentLimits: async (idToken: string) => {
    const res = await apiFetch('/admin/ec-payment-limits', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  adminSetEcPaymentLimits: async (body: Record<string, unknown>, idToken: string) => {
    const res = await apiFetch('/admin/ec-payment-limits', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return res.json()
  },
  adminGetFirestoreBudget: async (idToken: string) => {
    const res = await apiFetch('/admin/firestore-budget', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  // 1 オーナーあたりの月次**超過**上限。総予算をオーナー数で割る方式はやめた
  // ので、名前も総額と取り違えないものにしてある (AI 側は従来どおり総予算)。
  adminSetFirestoreBudget: async (perOwnerOverageCapJpy: number, idToken: string) => {
    const res = await apiFetch('/admin/firestore-budget', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ perOwnerOverageCapJpy }),
    })
    return res.json()
  },
  adminRegenerateFirestoreSnapshot: async (idToken: string) => {
    const res = await apiFetch('/admin/firestore-budget/regenerate-snapshot', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  adminUnblockFirestoreOwner: async (ownerId: string, idToken: string) => {
    const res = await apiFetch('/admin/firestore-budget/unblock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ ownerId }),
    })
    return res.json()
  },
  adminBlockFirestoreOwner: async (ownerId: string, idToken: string) => {
    const res = await apiFetch('/admin/firestore-budget/block', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ ownerId }),
    })
    return res.json()
  },
  adminListFirestoreUnblockRequests: async (idToken: string): Promise<{
    requests: Array<{
      ownerId: string
      ownerName: string
      ownerEmail?: string
      month: string
      requestedAt: string | null
      reason: string | null
      costJpy: number
      totalLimitJpy: number
      reads: number
      writes: number
    }>
  }> => {
    const res = await apiFetch('/admin/firestore-budget/unblock-requests', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  adminResolveFirestoreUnblockRequest: async (
    ownerId: string,
    approve: boolean,
    idToken: string,
  ) => {
    const res = await apiFetch('/admin/firestore-budget/unblock-requests/resolve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ ownerId, approve }),
    })
    return res.json()
  },
  submitFirestoreUnblockRequest: async (reason: string | undefined, idToken: string) => {
    const res = await apiFetch('/usage/owner/me/unblock-request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ reason: reason ?? null }),
    })
    return res.json()
  },

  // #2024 返金が失敗している予約 — 一覧 + 今すぐ再試行。backend: services/pay/api/routers/admin.py
  adminListFailedRefunds: async (
    idToken: string,
  ): Promise<{ items: AdminFailedRefund[]; autoRetryLimit: number; tickets?: AdminFailedTicketRefund[];
    onlineClaims?: AdminOnlineRefundClaim[] }> => {
    const res = await apiFetch('/admin/refunds/failed', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  // #2308 ② オンラインの返金の「届いていない」: やり直す (払われていない) / 確かめた (払われている)
  adminReopenOnlineRefund: async (voucherId: string, idToken: string) => {
    const res = await apiFetch(`/admin/refunds/online/${encodeURIComponent(voucherId)}/reopen`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  adminConfirmOnlineRefund: async (voucherId: string, idToken: string) => {
    const res = await apiFetch(`/admin/refunds/online/${encodeURIComponent(voucherId)}/confirm`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  adminRetryRefund: async (
    reservationId: string,
    idToken: string,
  ): Promise<{
    refundStatus: string | null
    refundedAmount: number | null
    refundError: string | null
    refundIntendedAmount: number | null
  }> => {
    const res = await apiFetch(`/admin/refunds/${encodeURIComponent(reservationId)}/retry`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #2298 体験のチケットの返金を出し直す (運営が確かめてから)。409 = 出し直せない (code で理由)
  adminRetryTicketRefund: async (
    ticketId: string,
    idToken: string,
  ): Promise<{ refundStatus: string | null; refundedAmount: number | null; refundError: string | null }> => {
    const res = await apiFetch(`/admin/refunds/tickets/${encodeURIComponent(ticketId)}/retry`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #446 admin_alerts management — list + resolve. backend: services/pay/api/routers/admin.py
  adminListAlerts: async (
    params: { status?: 'unresolved' | 'resolved' | 'all'; limit?: number; cursor?: string | null },
    idToken: string,
  ): Promise<{
    rows: Array<{
      id: string
      type: string | null
      severity: string | null
      message: string | null
      payload: Record<string, unknown>
      createdAt: string | null
      resolvedAt: string | null
      resolutionType: string | null
      resolvedBy: string | null
      notes: string | null
    }>
    nextCursor: string | null
  }> => {
    const qs = new URLSearchParams()
    if (params.status) qs.set('status', params.status)
    if (params.limit) qs.set('limit', String(params.limit))
    if (params.cursor) qs.set('cursor', params.cursor)
    const res = await apiFetch(`/admin/alerts?${qs.toString()}`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },
  adminResolveAlert: async (
    alertId: string,
    body: { notes?: string; resolutionType?: 'manual' | 'auto' },
    idToken: string,
  ): Promise<{ ok: boolean; alertId: string }> => {
    const res = await apiFetch(`/admin/alerts/${encodeURIComponent(alertId)}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({
        notes: body.notes ?? null,
        resolutionType: body.resolutionType ?? 'manual',
      }),
    })
    return res.json()
  },

  // #528 退会監査 audit log 一覧 — backend: services/pay/api/routers/admin.py
  adminListAccountDeletions: async (
    params: {
      limit?: number
      cursor?: string | null
      outcome?: 'success' | 'partial' | 'failed' | null
      /** #1568 店舗オーナーの退会 / 顧客の退会 で絞る。 */
      subject?: 'store_owner' | 'user' | null
    },
    idToken: string,
  ): Promise<{
    rows: Array<{
      id: string
      uid: string | null
      subject: string | null  // 'store_owner' | 'user'
      stripeAccountId: string | null
      storesDeleted: number
      outcome: string | null
      ipAddress: string | null
      userAgent: string | null
      deletedAt: string | null
    }>
    nextCursor: string | null
  }> => {
    const qs = new URLSearchParams()
    if (params.limit) qs.set('limit', String(params.limit))
    if (params.cursor) qs.set('cursor', params.cursor)
    if (params.outcome) qs.set('outcome', params.outcome)
    if (params.subject) qs.set('subject', params.subject)
    const res = await apiFetch(`/admin/account-deletion-audit?${qs.toString()}`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #1568 本人照会 — メールから uid を割り出す。**氏名も残高も返らない。**
  adminAccountLookup: async (email: string, idToken: string): Promise<{
    userId: string
    exists: boolean
    withdrawn: boolean
    deletedAt: string | null
    auditCount: number
  }> => {
    const qs = new URLSearchParams({ email })
    const res = await apiFetch(`/admin/account-lookup?${qs.toString()}`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #1576 アカウント復旧 — 締め出し解除と退会の取り消し。理由は必須 (監査に残る)。
  adminAccountRecovery: async (
    params: {
      email: string
      action: 'reset_devices' | 'reset_credentials' | 'unenroll_mfa' | 'reinstate'
      reason: string
    },
    idToken: string,
  ): Promise<{ action: string; userId: string; detail: Record<string, unknown>; auditId: string }> => {
    const res = await apiFetch('/admin/account-recovery', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify(params),
    })
    return res.json()
  },

  // #1572 ログイン履歴 — 端末 + 認証ログ。**氏名は返らない。**
  adminAccountActivity: async (email: string, idToken: string): Promise<{
    userId: string
    devices: Array<{
      deviceId: string
      deviceLabel: string
      firstSeen: string | null
      lastSeen: string | null
      firstIpAddress: string | null
      lastIpAddress: string | null
      lastUserAgent: string | null
      trusted: boolean
      trustedAt: string | null
      trustedVia: string | null
    }>
    authLogs: Array<{
      id: string
      authenticatedAt: string | null
      ip: string | null
      userAgent: string | null
      credentialDeviceType: string | null
      userVerified: boolean | null
      paymentIntentId: string | null
    }>
  }> => {
    const qs = new URLSearchParams({ email })
    const res = await apiFetch(`/admin/account-activity?${qs.toString()}`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #1568 登録・退会の推移 — backend: services/pay/api/routers/admin.py
  adminGrowthStats: async (
    params: { days?: number },
    idToken: string,
  ): Promise<{
    days: Array<{
      date: string
      newUsers: number
      newStores: number
      withdrawnUsers: number
      withdrawnStores: number
    }>
    totals: { users: number; stores: number; storeOwners: number }
  }> => {
    const qs = new URLSearchParams()
    if (params.days) qs.set('days', String(params.days))
    const res = await apiFetch(`/admin/growth-stats?${qs.toString()}`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  /**
   * #1998 登録ユーザーの属性の内訳（国籍 / 年代 / 性別 / 利用言語）。
   *
   * **区分ごとの人数だけ**が返る。誰がどの区分かは返らない（自治体へ渡すのも
   * この集計値なので、個人情報の第三者提供には当たらない）。
   * `value` が `__unknown__` の区分は未回答。
   */
  adminUserAttributes: async (
    idToken: string,
  ): Promise<{
    total: number
    generatedAt: string
    breakdown: Record<string, Array<{ value: string; count: number }>>
  }> => {
    const res = await apiFetch('/admin/user-attributes', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #585 follow-up 退会監査2 — store_owners の soft-deleted entry + Stripe purge 状態。
  adminListAccountPurgeStatus: async (
    params: { limit?: number; cursor?: string | null },
    idToken: string,
  ): Promise<{
    rows: Array<{
      uid: string
      email: string | null
      ownerName: string | null
      stripeAccountId: string | null
      storesCount: number
      deletedAt: string | null
      stripeAccountPurgedAt: string | null
      daysSinceDeleted: number | null
      status: 'withdrawn' | 'purged' | 'pending_failed'
      purgeAfterDays: number
    }>
    nextCursor: string | null
  }> => {
    const qs = new URLSearchParams()
    if (params.limit) qs.set('limit', String(params.limit))
    if (params.cursor) qs.set('cursor', params.cursor)
    const res = await apiFetch(`/admin/account-purge-status?${qs.toString()}`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #52 owner-side AI quota lookup — powers the AI half of the owner
  // dashboard's quota widget.
  getOwnerAiUsage: async (
    idToken: string,
  ): Promise<{
    ownerId: string
    month: string
    inputTokens: number
    outputTokens: number
    thoughtsTokens: number
    callCount: number
    costJpy: number
    limitJpy: number
    usedPercent: number
    blocked: boolean
    warning80Sent: boolean
    resetDate: string
    // #1552 Firestore 枠と同じ形。**無料枠と上限は別の数字**。
    capJpy: number
    capMinJpy: number
    freeAllowanceJpy: number
    billingEnabled: boolean
  }> => {
    const res = await apiFetch('/ai/usage/owner/me/month', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #1552 オーナーが自分の AI 月次上限を設定する。**自動延長のスイッチは
  // Firestore 枠と共通**なので、こちらには無い（枠ごとに分けると
  // 「AI だけ止まった」が起きて分かりにくい）。
  updateOwnerAiCap: async (
    capJpy: number,
    idToken: string,
  ): Promise<{ ownerId: string; capJpy: number; capMinJpy: number }> => {
    const res = await apiFetch('/ai/usage/owner/me/cap', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ capJpy }),
    })
    return res.json()
  },

  // #296 owner-side Firestore quota lookup (per-owner, mirrors AI quota)
  getOwnerFirestoreUsage: async (
    idToken: string,
  ): Promise<{
    ownerId: string
    month: string
    reads: number
    writes: number
    costJpy: number
    limitJpy: number
    totalLimitJpy: number
    usedPercent: number
    buckets: unknown[]
    blocked: boolean
    warning80Sent: boolean
    resetDate: string
    billingEnabled: boolean
    todayReads: number
    todayWrites: number
    dailyDate: string | null
    freeTierReadsPerDay: number
    freeTierWritesPerDay: number
    unblockRequestPending: boolean
    // #1547 オーナーが自分で決める月次上限と、自動延長の ON/OFF。
    // **この超過分は店舗オーナーが払う**ので、上限に天井は無い（下限のみ）。
    capJpy: number
    capMinJpy: number
    /** 弊社が無償で持つ分。ここまでは請求されない。 */
    freeAllowanceJpy: number
    autoExtend: boolean
  }> => {
    const res = await apiFetch('/usage/owner/me/month', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #1547 オーナーが自分の月次上限を設定する。下限未満はサーバ側で clamp される
  // ので、返ってきた capJpy を正としてそのまま表示する。
  updateOwnerFirestoreCap: async (
    capJpy: number,
    idToken: string,
  ): Promise<{ ownerId: string; capJpy: number; capMinJpy: number }> => {
    const res = await apiFetch('/usage/owner/me/cap', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ capJpy }),
    })
    return res.json()
  },

  // #1547 店舗オーナーの支払い方法（カード）。**弊社がオーナーから自動でお金を
  // 取るための基盤**で、超過分の回収だけでなく将来の月額サブスクも同じカードに乗る。
  listOwnerCards: async (
    idToken: string,
  ): Promise<{ cards: Array<{ id: string; brand: string; last4: string; expMonth: number; expYear: number }> }> => {
    const res = await apiFetch('/billing/owner/me/cards', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // カード登録用の SetupIntent。**支払いより先に登録させる**ので PaymentIntent
  // ではなく SetupIntent（登録が済んでいないと自動延長を ON にできない）。
  createOwnerCardSetupIntent: async (
    idToken: string,
  ): Promise<{ clientSecret: string; customerId: string }> => {
    const res = await apiFetch('/billing/owner/me/setup-intent', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  deleteOwnerCard: async (paymentMethodId: string, idToken: string): Promise<void> => {
    await apiFetch(`/billing/owner/me/cards/${paymentMethodId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
  },

  // #1547 自動延長の ON/OFF。**ON にする行為が「超過分を請求してよい」という同意**
  // なので、呼び出す前に金額が発生することを画面で明示すること。
  // 既に止まっている状態で ON にすると、その場で引き落として解除される (unblocked)。
  updateOwnerFirestoreAutoExtend: async (
    enabled: boolean,
    idToken: string,
  ): Promise<{ ownerId: string; autoExtend: boolean; unblocked: boolean }> => {
    const res = await apiFetch('/usage/owner/me/auto-extend', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ enabled }),
    })
    return res.json()
  },
  // #2121 代行掲載 (弊社が預かって載せている店)。backend: services/pay/api/routers/admin.py
  adminListAgencyListings: async (
    idToken: string,
  ): Promise<{ items: AdminAgencyListing[] }> => {
    const res = await apiFetch('/admin/agency-listings', {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // **まだ記録が無い店 ID でも 200 で返る** (画面がこれを初期値にして新規に書く)。
  // 404 は「その店が無い」ときだけ。
  adminGetAgencyListing: async (
    storeId: string,
    idToken: string,
  ): Promise<AdminAgencyListing> => {
    const res = await apiFetch(`/admin/agency-listings/${encodeURIComponent(storeId)}`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  adminSaveAgencyListing: async (
    storeId: string,
    input: AdminAgencyListingInput,
    idToken: string,
  ): Promise<AdminAgencyListing> => {
    const res = await apiFetch(`/admin/agency-listings/${encodeURIComponent(storeId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify(input),
    })
    return res.json()
  },

  // 店に渡した。区分が店舗運用に戻り、客の画面の「YOKOSO PAY が掲載」も消える
  adminHandoverAgencyListing: async (
    storeId: string,
    idToken: string,
  ): Promise<AdminAgencyListing> => {
    const res = await apiFetch(
      `/admin/agency-listings/${encodeURIComponent(storeId)}/handover`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}` },
      })
    return res.json()
  },
}
